import React, { FC, useState } from "react";
import { Button, Flex, Notification, Text, toast } from "@telia/teddy";
import { useDispatch, useSelector } from "react-redux";
import { FORM_ID } from "../../../../../constants";
import { RootState } from "../../../../../models";
import { post as Ajax_post } from "../../../app/Ajax";
import { FieldSet, Input } from "../../../forms";
import { RapidForm } from "../../../rapidform";
import { VALIDATION_FIELD } from "../../../rapidform/formValidation";
import { clearAll } from "../../../rapidform/state/actions";
import { PhoneNumberFormatters } from "../../../app/utils";
import { API_URL_REGISTER_IMEI } from "../../api";
import { getFormById } from "../../state/selectors/formSelectors";
import { hasDisplayableMessage } from "../../utils";

const formatPhone = (phoneNumber: string | number): string => {
    const onlyDigits = String(phoneNumber).replace(/\D/g, "");
    const digits = onlyDigits.replace(/^0047/, "").replace(/^47/, "");
    const formatter = PhoneNumberFormatters.find((f) => f.displayFormat === "XXX XX XXX");
    return formatter ? formatter.displayFn(digits) : digits;
};

interface ImeiRegistrationFormProps {
    offeringName?: string;
    onCancel: () => void;
    onSuccess: () => void;
    orderId: number;
    phoneNumber?: string | number;
}

const ImeiRegistrationForm: FC<ImeiRegistrationFormProps> = ({
    orderId,
    phoneNumber,
    offeringName,
    onCancel,
    onSuccess,
}) => {
    const dispatch = useDispatch();
    const form = useSelector((state: RootState) => getFormById(state, FORM_ID.IMEI_REGISTRATION));
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const imeiValue = form?.values?.imeiDetails?.[VALIDATION_FIELD.IMEI] || "";
    const phone = phoneNumber ? String(phoneNumber) : form?.values?.imeiDetails?.[VALIDATION_FIELD.PHONE] || "";
    const clearInput = () => dispatch(clearAll(FORM_ID.IMEI_REGISTRATION));

    const handleSubmit = async () => {
        setErrorMessage(null);
        setIsSubmitting(true);

        try {
            await Ajax_post({
                url: API_URL_REGISTER_IMEI,
                data: { imei: imeiValue, orderId, phoneNumber: phone },
            });
            clearInput();
            toast.success("IMEI-nummer registrert", { position: "top-center" });
            onSuccess();
        } catch (err) {
            if (hasDisplayableMessage(err)) {
                setErrorMessage(err.displayableMessage);
            } else if (err instanceof Error) {
                setErrorMessage(err.message);
            } else {
                setErrorMessage("Kunne ikke registrere IMEI. Prøv igjen senere.");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="orders__imei-form">
            {(phoneNumber || offeringName) && (
                <Flex direction="column" gap="50" mt="200">
                    {phoneNumber && (
                        <Text>
                            <span className="telia-sans-medium">Telefonnummer:</span>{" "}
                            <span data-di-mask>{formatPhone(phoneNumber)}</span>
                        </Text>
                    )}
                    {offeringName && (
                        <Text>
                            <span className="telia-sans-medium">Abonnement:</span> {offeringName}
                        </Text>
                    )}
                </Flex>
            )}
            <RapidForm
                id={FORM_ID.IMEI_REGISTRATION}
                action={"#"}
                values={form?.values || {}}
                visible={form?.visible || {}}
                started={form?.started || {}}
                errors={form?.errors || {}}
                onSubmit={handleSubmit}
            >
                <FieldSet name="imeiDetails" noMargin>
                    <Flex gap="200" align="flex-start">
                        <Input
                            type="text"
                            name={VALIDATION_FIELD.IMEI}
                            label="IMEI-nummer"
                            placeholder="Nøyaktig 15 siffer"
                        />
                        {!phoneNumber && (
                            <Input
                                type="text"
                                name={VALIDATION_FIELD.PHONE}
                                label="Telefonnummer"
                                placeholder="F.eks. 12345678"
                            />
                        )}
                        <Flex gap="100" className="orders__imei-actions">
                            <Button
                                type="submit"
                                variant="primary"
                                size="sm"
                                disabled={isSubmitting}
                                loading={isSubmitting}
                            >
                                {isSubmitting ? "Registrerer..." : "Registrer"}
                            </Button>
                            <Button
                                type="button"
                                variant="text"
                                size="sm"
                                onClick={() => {
                                    clearInput();
                                    onCancel();
                                }}
                                disabled={isSubmitting}
                            >
                                Avbryt
                            </Button>
                        </Flex>
                    </Flex>
                </FieldSet>
            </RapidForm>
            {errorMessage && (
                <Notification type="tile" variant="error" mt="100">
                    <Notification.Icon />
                    <Notification.Text>{errorMessage}</Notification.Text>
                </Notification>
            )}
        </div>
    );
};

export default ImeiRegistrationForm;
